import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Code2, ExternalLink, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '@/financeBotV2/src/components/ui/button';
import { Input } from '@/financeBotV2/src/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/financeBotV2/src/components/ui/select';
import { Textarea } from '@/financeBotV2/src/components/ui/textarea';
import {
  preCannedReportsApi,
  PreCannedReportTemplate,
  PreCannedReportTemplateSummary,
  withInventoryFiscalPeriod,
} from '@/services/preCannedReportsService';

const PERIOD_OPTIONS = [
  { label: 'Current Period', value: 'current month' },
  { label: 'Prior Period', value: 'last month' },
  { label: 'YTD (Year to Date)', value: 'YTD' },
];

const COMPARATOR_OPTIONS = [
  { label: 'Prior Fiscal Year', value: 'Last Year' },
];

const buildInitialParameters = (template?: PreCannedReportTemplateSummary | null): Record<string, string> => {
  if (!template) return {};
  const out: Record<string, string> = {};
  template.filters.forEach((filter) => {
    const defaultValue = filter.id === 'tool_period'
      ? 'YTD'
      : filter.id === 'comparison'
        ? 'Last Year'
        : filter.default ?? template.defaults?.[filter.id] ?? '';
    out[filter.id] = String(defaultValue ?? '');
  });
  return out;
};

const patchJsonControls = (jsonText: string, updates: Record<string, string>): string => {
  if (!jsonText.trim()) return jsonText;
  try {
    const parsed = JSON.parse(jsonText) as Record<string, unknown>;
    const controls = typeof parsed.controls === 'object' && parsed.controls !== null && !Array.isArray(parsed.controls)
      ? parsed.controls as Record<string, unknown>
      : null;
    const defaults = typeof parsed.defaults === 'object' && parsed.defaults !== null && !Array.isArray(parsed.defaults)
      ? parsed.defaults as Record<string, unknown>
      : null;
    if (controls) parsed.controls = { ...controls, ...updates };
    if (defaults) parsed.defaults = { ...defaults, ...updates };
    return JSON.stringify(parsed, null, 2);
  } catch {
    return jsonText;
  }
};

const isPendingValidationDiagnostic = (item: Record<string, unknown>) => {
  const tool = String(item.tool ?? '');
  const source = String(item.source ?? '');
  const message = String(item.message ?? '');
  return tool.startsWith('missing.')
    || source.endsWith('_gap')
    || message.includes('Metric unavailable pending source/tool validation');
};

const getFixedControlOptions = (filterId: string) => {
  if (filterId === 'tool_period') return PERIOD_OPTIONS;
  if (filterId === 'comparison') return COMPARATOR_OPTIONS;
  return null;
};

export const PreCannedReportSettingsTab: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<PreCannedReportTemplateSummary[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [templateJson, setTemplateJson] = useState('');
  const [rawResponse, setRawResponse] = useState<Record<string, unknown> | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingTemplateJson, setLoadingTemplateJson] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoadingTemplates(true);
    preCannedReportsApi.listTemplates()
      .then((items) => {
        if (!mounted) return;
        setTemplates(items);
        const first = items.find((item) => item.id === 'smart_morning_finance') ?? items[0];
        if (first) {
          setSelectedTemplateId(first.id);
          setParameters(withInventoryFiscalPeriod(buildInitialParameters(first)));
        }
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Unable to load templates');
      })
      .finally(() => {
        if (mounted) setLoadingTemplates(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templates],
  );

  const hydrate = useCallback(async () => {
    if (!templateJson.trim()) return;
    setHydrating(true);
    setError(null);
    setJsonError(null);
    setSuccessMessage(null);
    try {
      const parsed = JSON.parse(templateJson) as PreCannedReportTemplate;
      let nextParameters = parameters;
      const toolPeriod = parameters.tool_period?.trim();
      if (toolPeriod) {
        const resolved = await preCannedReportsApi.resolvePeriod(toolPeriod);
        const updates = withInventoryFiscalPeriod({
          tool_period: resolved.tool_period || toolPeriod,
          period_label: resolved.period_label,
          period: resolved.period,
          inventory_fiscal_period: resolved.inventory_fiscal_period,
        });
        nextParameters = { ...parameters, ...updates };
        setParameters(nextParameters);
        setTemplateJson((current) => patchJsonControls(current, updates));
      }
      const response = await preCannedReportsApi.previewTemplate(
        parsed,
        withInventoryFiscalPeriod(nextParameters),
        { forceRefresh: true },
      );
      setRawResponse(response);
      setSuccessMessage('Report JSON rendered successfully.');
    } catch (err) {
      if (err instanceof SyntaxError) {
        setJsonError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Unable to render report JSON');
      }
    } finally {
      setHydrating(false);
    }
  }, [parameters, templateJson]);

  const resolveToolPeriod = useCallback(async (value: string) => {
    const nextValue = value.trim();
    if (!nextValue) return;
    try {
      const resolved = await preCannedReportsApi.resolvePeriod(nextValue);
      const updates = withInventoryFiscalPeriod({
        tool_period: resolved.tool_period || nextValue,
        period_label: resolved.period_label,
        period: resolved.period,
        inventory_fiscal_period: resolved.inventory_fiscal_period,
      });
      setParameters((current) => ({ ...current, ...updates }));
      setTemplateJson((current) => patchJsonControls(current, updates));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to resolve period');
    }
  }, []);

  const updateControlValue = useCallback((filterId: string, value: string) => {
    setParameters((current) => ({ ...current, [filterId]: value }));
    setTemplateJson((current) => patchJsonControls(current, { [filterId]: value }));
    setSuccessMessage(null);
    if (filterId === 'tool_period') {
      void resolveToolPeriod(value);
    }
  }, [resolveToolPeriod]);

  const loadTemplateJson = useCallback(async (templateId: string, nextParameters: Record<string, string>) => {
    setLoadingTemplateJson(true);
    setError(null);
    setJsonError(null);
    setSuccessMessage(null);
    try {
      const template = await preCannedReportsApi.getAuthoringTemplate(templateId);
      setTemplateJson(patchJsonControls(JSON.stringify(template, null, 2), nextParameters));
      const response = await preCannedReportsApi.previewTemplate(template, withInventoryFiscalPeriod(nextParameters));
      setRawResponse(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load report JSON');
    } finally {
      setLoadingTemplateJson(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTemplateId && selectedTemplate) {
      const nextParameters = withInventoryFiscalPeriod(buildInitialParameters(selectedTemplate));
      setParameters(nextParameters);
      void loadTemplateJson(selectedTemplateId, nextParameters);
    }
  }, [loadTemplateJson, selectedTemplate, selectedTemplateId]);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setRawResponse(null);
  };

  const openDashboard = () => {
    if (!selectedTemplateId) return;
    const search = new URLSearchParams();
    Object.entries(parameters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim()) {
        search.set(key, String(value));
      }
    });
    navigate(`/smart-morning-for-leaders/render/${encodeURIComponent(selectedTemplateId)}?${search.toString()}`);
  };

  const resetTemplateJson = () => {
    if (!selectedTemplateId || !selectedTemplate) return;
    const nextParameters = withInventoryFiscalPeriod(buildInitialParameters(selectedTemplate));
    setParameters(nextParameters);
    void loadTemplateJson(selectedTemplateId, nextParameters);
  };

  const visibleFilters = (selectedTemplate?.filters ?? []).filter((filter) => !['period', 'period_label'].includes(filter.id));
  const responseErrors = Array.isArray(rawResponse?.errors) ? rawResponse.errors as Array<Record<string, unknown>> : [];
  const visibleResponseErrors = responseErrors.filter((item) => !isPendingValidationDiagnostic(item));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Pre-Canned Report</h2>
        <p className="text-sm text-slate-600">Manage template controls and editable JSON for pre-canned report dashboards.</p>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-6 py-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Report Controls</h3>
        </div>
        <div className="grid gap-4 p-6 xl:grid-cols-[minmax(18rem,24rem)_minmax(0,1fr)_auto] xl:items-end">
          <label className="space-y-1.5">
            <span className="text-xs font-semibold uppercase text-slate-600">Template</span>
            <Select value={selectedTemplateId} onValueChange={handleTemplateChange} disabled={loadingTemplates || templates.length === 0}>
              <SelectTrigger className="h-10 rounded-md border-slate-200 bg-white">
                <SelectValue placeholder={loadingTemplates ? 'Loading templates...' : 'Select a template'} />
              </SelectTrigger>
              <SelectContent>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <div className="grid gap-3 md:grid-cols-3">
            {visibleFilters.map((filter) => (
              <label key={filter.id} className="space-y-1.5">
                <span className="text-xs font-semibold uppercase text-slate-600">{filter.label}</span>
                {getFixedControlOptions(filter.id) || (filter.type === 'select' && Array.isArray(filter.options) && filter.options.length > 0) ? (
                  <Select
                    value={parameters[filter.id] ?? ''}
                    onValueChange={(value) => updateControlValue(filter.id, value)}
                  >
                    <SelectTrigger className="h-10 rounded-md border-slate-200 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(getFixedControlOptions(filter.id) ?? filter.options ?? []).map((option) => {
                        const optionValue = String(option.value ?? '');
                        return (
                          <SelectItem key={optionValue} value={optionValue}>
                            {String(option.label ?? optionValue)}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={parameters[filter.id] ?? ''}
                    onChange={(event) => {
                      updateControlValue(filter.id, event.target.value);
                    }}
                    onBlur={(event) => {
                      if (filter.id === 'tool_period') {
                        void resolveToolPeriod(event.target.value);
                      }
                    }}
                    className="h-10 rounded-md border-slate-200 bg-white"
                  />
                )}
              </label>
            ))}
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" disabled={!selectedTemplateId} onClick={openDashboard} className="h-10 rounded-md border-slate-200 bg-white px-4 text-slate-700">
              <ExternalLink className="mr-2 h-4 w-4" />
              Dashboard
            </Button>
            <Button type="button" disabled={!templateJson.trim() || hydrating || loadingTemplateJson} onClick={() => void hydrate()} className="h-10 rounded-md bg-primary-600 px-4 text-white hover:bg-primary-700">
              <RefreshCw className={`mr-2 h-4 w-4 ${hydrating ? 'animate-spin' : ''}`} />
              Render JSON
            </Button>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <Code2 className="h-4 w-4 text-primary-600" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Editable Report JSON</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={!selectedTemplateId || loadingTemplateJson}
              onClick={resetTemplateJson}
              className="h-8 rounded-md border-slate-200 bg-white px-3 text-xs text-slate-700"
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Reset
            </Button>
            <Button
              type="button"
              disabled={!templateJson.trim() || hydrating}
              onClick={() => void hydrate()}
              className="h-8 rounded-md bg-primary-600 px-3 text-xs text-white hover:bg-primary-700"
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${hydrating ? 'animate-spin' : ''}`} />
              Render
            </Button>
          </div>
        </div>
        <div className="space-y-3 p-6">
          <Textarea
            value={templateJson}
            onChange={(event) => {
              setTemplateJson(event.target.value);
              setJsonError(null);
              setSuccessMessage(null);
            }}
            spellCheck={false}
            className="min-h-[360px] resize-y rounded-md border-slate-200 bg-slate-950 font-mono text-[12px] leading-5 text-slate-100 shadow-inner focus-visible:ring-primary-600/30"
            placeholder={loadingTemplateJson ? 'Loading report JSON...' : 'Edit the report JSON, then render it.'}
          />

          {jsonError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              Invalid JSON: {jsonError}
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {successMessage && !error && !jsonError && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}
          {visibleResponseErrors.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {visibleResponseErrors.map((item, index) => (
                <div key={`${String(item.source ?? 'error')}-${index}`}>
                  {String(item.source ?? 'Source')}: {String(item.message ?? 'Hydration warning')}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PreCannedReportSettingsTab;
